import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeSlash, ArrowRight } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { LOGO_URL } from '../lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendNote('');
    setNeedsVerification(false);
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      const role = result.user?.role;
      if (role === 'admin') navigate('/admin');
      else if (role === 'organizer') navigate('/organizer');
      else navigate('/player-dashboard');
    } else if (result.code === 'email_not_verified') {
      setNeedsVerification(true);
      setError(result.error);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendBusy(true);
    setResendNote('');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResendNote('Verification email resent. Please check your inbox (and Spam folder).');
    } catch {
      setResendNote('Could not resend right now. Please try again in a moment.');
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <>
      <SEO title="Login" description="Sign in to your Beyond Ball India account to register for tournaments and manage your basketball profile." path="/login" />
      <div className="min-h-screen flex items-center justify-center px-4 py-24 court-pattern">
        <div className="max-w-md w-full">
          <div className="text-center mb-8 animate-fade-up">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Beyond Ball India" className="h-14 w-14 rounded-full object-contain mx-auto" />
            </Link>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-2" data-testid="login-title">
              Welcome back
            </h1>
            <p className="text-gray-600">Sign in to your account</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm animate-fade-up delay-100">
            <GoogleAuthButton label="Continue with Google" testId="login-google-btn" />
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">or with email</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {error && (
              <div className={`px-4 py-3 mb-4 text-sm rounded-xl ${needsVerification ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-red-50 border border-red-100 text-red-700'}`} data-testid="login-error">
                {error}
                {needsVerification && (
                  <div className="mt-3 flex flex-col gap-2">
                    {resendNote && (
                      <div className="text-xs text-gray-700" data-testid="login-resend-note">{resendNote}</div>
                    )}
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendBusy}
                      className="self-start bg-amber-600 hover:bg-amber-700 text-white px-3.5 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
                      data-testid="login-resend-verification-btn"
                    >
                      {resendBusy ? 'Sending...' : 'Resend verification email'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl text-gray-900 px-4 py-3 text-sm outline-none transition-all"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-semibold text-gray-700">Password</label>
                  <Link to="/forgot-password" className="text-xs text-[#F97316] hover:text-orange-700 font-semibold" data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl text-gray-900 px-4 py-3 pr-12 text-sm outline-none transition-all"
                    required
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1" data-testid="toggle-password-visibility">
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                {!loading && <ArrowRight size={16} weight="bold" />}
              </button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-gray-600">New to Beyond Ball India? </span>
              <Link to="/register" className="text-[#F97316] hover:text-orange-700 font-semibold" data-testid="register-link">
                Create account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
